import { fetchers } from "@/services/api/fetchers";
import { AddNote, AddNoteRes, FetchedNote, UpdateNoteRes } from "@/services/api/fetchers/notes";
import { performOptimisticAction } from "@/shared/utils/store";
import { storage } from "@/shared/utils/local-storage";
import { StateCreator } from "zustand";
import { BoundStore } from "./index";

export interface NotesSlice {
  notes: {
    deleted: FetchedNote[];
    main: FetchedNote[];
    addNote: (note: AddNote) => Promise<AddNoteRes | undefined>;
    deleteNote: (noteId: number) => Promise<void>;
    fetchDeletedNotes: () => Promise<FetchedNote[]>;
    fetchNotes: (fetchAll?: boolean) => Promise<FetchedNote[]>;
    fetchNoteById: (noteId: number) => Promise<FetchedNote | undefined>;
    removeHardDeletedNotes: () => Promise<void>;
    removeNote: (noteId: number) => void;
    restoreNote: (noteId: number) => void;
    tempDeleteNote: (noteId: number) => Promise<void>;
    updateNote: (
      noteId: number,
      updatedNote: Partial<Omit<FetchedNote, "id">>,
      shared: boolean
    ) => Promise<UpdateNoteRes | null>;
  };
}

const createNotesSlice: StateCreator<BoundStore, [], [], NotesSlice> = (set, get) => ({
  notes: {
    main: [],
    deleted: [],

    addNote: async (note) => {
      const existingMainNotes = get().notes.main;
      const tempId = Date.now();

      return await performOptimisticAction({
        optimisticUpdate: () =>
          set((state) => {
            return {
              ...state,
              notes: {
                ...state.notes,
                main: [
                  {
                    ...note,
                    id: tempId,
                    tags: [],
                    public_uuid: null,
                    pinned: false,
                    locked: false,
                    masked: false,
                    encrypted_data: "",
                    iv: "",
                    soft_deleted_at: null,
                  },
                  ...state.notes.main,
                ],
              },
            };
          }),
        apiCall: async () => {
          const addedNote = await fetchers.notes.addNote(note);
          set((state) => ({
            notes: {
              ...state.notes,
              main: state.notes.main.map((n) => (n.id === tempId ? { ...addedNote, id: addedNote.id, tags: [] } : n)),
            },
          }));

          return addedNote;
        },
        rollback: () =>
          set((state) => ({
            ...state,
            notes: {
              ...state.notes,
              main: existingMainNotes,
            },
          })),
      });
    },

    deleteNote: async (noteId) => {
      // Added async
      const existingMainNotes = get().notes.main;
      const existingDeletedNotes = get().notes.deleted;

      await performOptimisticAction({
        // Added await
        optimisticUpdate: () =>
          set((state) => ({
            notes: {
              ...state.notes,
              main: state.notes.main.filter((note) => note.id !== noteId),
              deleted: state.notes.deleted.filter((note) => note.id !== noteId),
            },
          })),
        apiCall: async () => fetchers.notes.hardDeleteNote(noteId),
        rollback: () =>
          set((state) => ({
            notes: {
              ...state.notes,
              main: existingMainNotes,
              deleted: existingDeletedNotes,
            },
          })),
      });
    },

    fetchDeletedNotes: async () => {
      try {
        const deleted = await fetchers.notes.getDeletedNotes();
        set((state) => ({
          notes: {
            ...state.notes,
            deleted,
          },
        }));
        return deleted;
      } catch (error) {
        console.error("Failed to fetch deleted notes:", error);
        return [];
      }
    },

    fetchNotes: async (fetchAll = false) => {
      try {
        const lastSync = fetchAll ? null : storage.getLastSync();

        // Fetch notes (either all or only updated since last sync)
        const fetchedNotes = await fetchers.notes.fetchNotes(lastSync);

        // Separate soft-deleted notes from regular notes
        const activeNotes = fetchedNotes.filter((n) => !n.soft_deleted_at);
        const softDeletedNotes = fetchedNotes.filter((n) => n.soft_deleted_at);

        let notes: FetchedNote[];
        let deleted: FetchedNote[];

        if (lastSync) {
          // Merge updated notes with existing notes, preserving original order
          const existingNotes = get().notes.main;
          const existingDeleted = get().notes.deleted;
          const activeNotesMap = new Map(activeNotes.map((n) => [n.id, n]));
          const softDeletedMap = new Set(softDeletedNotes.map((n) => n.id));

          // Replace updated notes in their original positions, add new ones at the start
          const updatedExistingIds = new Set(existingNotes.map((n) => n.id));
          const newNoteIds = new Set(activeNotes.map((n) => n.id).filter((id) => !updatedExistingIds.has(id)));

          notes = [
            // New notes first (ordered by server's order)
            ...activeNotes.filter((n) => newNoteIds.has(n.id)),
            // Existing notes with updates applied, excluding those now deleted
            ...existingNotes.map((n) => activeNotesMap.get(n.id) || n).filter((n) => !softDeletedMap.has(n.id)),
          ];

          // Merge deleted notes and remove any that are now restored
          const activeIdsSet = new Set(activeNotes.map((n) => n.id));
          deleted = [
            ...softDeletedNotes,
            ...existingDeleted.filter((n) => !softDeletedMap.has(n.id) && !activeIdsSet.has(n.id)),
          ];
        } else {
          // First time - separate all fetched notes
          notes = activeNotes;
          deleted = softDeletedNotes;
        }

        set((state) => ({
          notes: {
            ...state.notes,
            main: notes,
            deleted: deleted,
          },
        }));

        return notes;
      } catch (error) {
        console.error("Failed to fetch notes:", error);
        // On error, return existing notes to prevent data loss
        // This prevents local notes from being overwritten when API fails
        return get().notes.main;
      }
    },

    removeHardDeletedNotes: async () => {
      try {
        const lastSync = storage.getLastSync();
        if (!lastSync) return;

        const hardDeletedIds = await fetchers.notes.fetchHardDeletedNotes(lastSync);
        if (hardDeletedIds.length === 0) return;

        set((state) => ({
          notes: {
            ...state.notes,
            main: state.notes.main.filter((note) => !hardDeletedIds.includes(note.id)),
            deleted: state.notes.deleted.filter((note) => !hardDeletedIds.includes(note.id)),
          },
        }));
      } catch (error) {
        console.error("Failed to remove hard deleted notes:", error);
      }
    },

    fetchNoteById: async (noteId) => {
      try {
        const note = await fetchers.notes.fetchNoteById(noteId);
        if (!note) return undefined;

        set((state) => ({
          notes: {
            ...state.notes,
            main: state.notes.main.map((n) => (n.id === noteId ? note : n)),
          },
        }));
        return note;
      } catch (error) {
        console.error("Failed to fetch note by ID:", error);
        return undefined;
      }
    },

    removeNote: (noteId) =>
      set((state) => ({
        notes: {
          ...state.notes,
          main: state.notes.main.filter((note) => note.id !== noteId),
        },
      })),

    restoreNote: (noteId) => {
      const existingMainNotes = get().notes.main;
      const existingDeletedNotes = get().notes.deleted;
      const noteToRestore = existingDeletedNotes.find((note) => note.id === noteId);

      if (!noteToRestore) return;

      performOptimisticAction({
        optimisticUpdate: () =>
          set((state) => ({
            notes: {
              ...state.notes,
              deleted: state.notes.deleted.filter((note) => note.id !== noteId),
              main: [noteToRestore, ...state.notes.main],
            },
          })),
        apiCall: async () => fetchers.notes.restoreNote(noteId),
        rollback: () =>
          set((state) => ({
            notes: {
              ...state.notes,
              main: existingMainNotes,
              deleted: existingDeletedNotes,
            },
          })),
      });
    },

    tempDeleteNote: async (noteId) => {
      // Added async
      const existingMainNotes = get().notes.main;
      const existingDeletedNotes = get().notes.deleted;
      const deletedNote = existingMainNotes.find((note) => note.id === noteId);

      if (!deletedNote) return;

      await performOptimisticAction({
        // Added await
        optimisticUpdate: () =>
          set((state) => ({
            notes: {
              ...state.notes,
              main: state.notes.main.filter((note) => note.id !== noteId),
              deleted: [{ ...deletedNote, folder_id: null }, ...state.notes.deleted],
            },
          })),
        apiCall: async () => fetchers.notes.tempDeleteNote(noteId),
        rollback: () =>
          set((state) => ({
            notes: {
              ...state.notes,
              main: existingMainNotes,
              deleted: existingDeletedNotes.filter((note) => note.id !== noteId),
            },
          })),
      });
    },

    updateNote: async (noteId, updatedNote, shared) => {
      const existingMainNotes = get().notes.main;
      const deletedNotes = get().notes.deleted;
      const previousNote =
        existingMainNotes.find((note) => note.id === noteId) || deletedNotes.find((note) => note.id === noteId);

      if (!previousNote) return null;

      return await performOptimisticAction({
        optimisticUpdate: () =>
          set((state) => ({
            notes: {
              ...state.notes,
              main: state.notes.main.map((note) => (note.id === noteId ? { ...note, ...updatedNote } : note)),
            },
          })),
        apiCall: async () => fetchers.notes.updateNote({ id: noteId, note: updatedNote, shared }),
        rollback: () =>
          set((state) => ({
            notes: {
              ...state.notes,
              main: existingMainNotes,
            },
          })),
      });
    },
  },
});

export default createNotesSlice;
